import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const agreementDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  budgetAllocation: z.array(z.string()).optional(),
  masterAgreement: z.array(z.string()).optional(),
  agreementNumber: z.array(z.string()).optional(),
  vistexConditionType: z.array(z.string()).optional(),
  effectiveDateFrom: z.coerce.date().optional(),
  effectiveDateTo: z.coerce.date().optional(),
  ...datatableSearchSchema.shape,
})

export type AgreementDetailsSearch = z.infer<typeof agreementDetailsSearchSchema>

export type Agreements = Awaited<ReturnType<typeof getAgreements>>

function normalizeQueries(queries: AgreementDetailsSearch) {
  const { effectiveDateFrom, effectiveDateTo, ...rest } = queries
  return stringifyValues({
    ...rest,
    effectiveDateFrom: effectiveDateFrom ? formatDateOnly(effectiveDateFrom) : undefined,
    effectiveDateTo: effectiveDateTo ? formatDateOnly(effectiveDateTo) : undefined,
  })
}

async function getAgreements(search: AgreementDetailsSearch = {}, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.agreements.$get(
    { query: normalizeQueries(search) },
    { init: { signal } },
  )

  return await res.json()
}

export function useAgreements(queries: AgreementDetailsSearch = {}) {
  return useQuery({
    queryKey: ['agreements', queries],
    queryFn: ({ signal }) => getAgreements(queries, signal),
  })
}

async function getAgreementSummary(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.agreements.summary.$get({}, { init: { signal } })
  return await res.json()
}

export const agreementSummaryQueryOptions = {
  queryKey: ['agreementSummary'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getAgreementSummary(signal),
}

export function useAgreementSummary() {
  return useQuery(agreementSummaryQueryOptions)
}

async function getAgreementDetailsForExport(
  queries: AgreementDetailsSearch = {},
  signal?: AbortSignal,
) {
  const stringifiedQueries = normalizeQueries(queries)
  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  return fetchCsvExport(`/crt/agreements/export?${queryParams}`, signal)
}

export const agreementDetailsExportQueryOptions = (queries: AgreementDetailsSearch = {}) => ({
  queryKey: ['agreementDetailsExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getAgreementDetailsForExport(queries, signal),
})

async function getAgreementDetailsForRawExport(signal?: AbortSignal) {
  return fetchCsvExport('/crt/agreements/raw-export', signal)
}

export const agreementDetailsRawExportQueryOptions = {
  queryKey: ['agreementDetailsRawExport'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getAgreementDetailsForRawExport(signal),
}
